import EmptyImage from "@/assets/images/emptyActionItem.svg";
import ConvertToTask from "@/components/recap/convertToTask/ConvertToTask";
import ConfirmPopoverButton from "@/components/shared/DeleteWithConfirmButton";
import IconGenerator from "@/components/shared/IconGenerator";
import RecapActionItemsSkeleton from "@/components/skeleton/RecapActionItemsSkeleton";
import {
  useRecapDeleteActionItem,
  useRecapUndoDeleteActionItem,
} from "@/features/hooks/useRecap";
import QueryKeys from "@/features/queryKeys";
import useResponsive from "@/hooks/useResponsive.hook";
import { IActionItems, RecapActionItemsProps } from "@/interfaces/IRecap";
import { openTaskUrl } from "@/middleware";
import { DateFormats } from "@/middleware/DateFormatter";
import { useAppActions } from "@/store/selectors/AppSelectors";
import {
  DeleteConfirmButton,
  Empty,
  Icon,
  Person,
  Tooltip,
} from "@nexus/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Drawer, Flex, Image, List, Table, Typography } from "antd";
import moment from "moment-jalaali";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type PriorityColor = "secondary" | "danger" | "warning" | "primary";

const priorityColorVar: Record<PriorityColor, string> = {
  secondary: "var(--color-disabled)",
  danger: "var(--color-error)",
  warning: "var(--color-warning)",
  primary: "var(--color-primary)",
};

const getPriorityMeta = (priority?: number | string) => {
  switch (priority) {
    case 1:
    case "1":
      return { color: "danger" as PriorityColor, key: "recap.highPriority" };
    case 2:
    case "2":
      return { color: "warning" as PriorityColor, key: "recap.mediumPriority" };
    case 3:
    case "3":
      return { color: "primary" as PriorityColor, key: "recap.lowPriority" };
    default:
      return null;
  }
};

const { Text, Paragraph } = Typography;

const ActionItems = ({
  actionItems = [],
  isLoading = false,
  sessionId,
  onEdit,
  showDeleted = false,
}: RecapActionItemsProps) => {
  const { t } = useTranslation();
  const isResponsive = useResponsive();
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();
  const getUserImage = useMemo(
    () => (userId?: string) => {
      if (!userId) return "";

      return `${(
        window._env_?.VITE_BASE_API_URL || import.meta.env.VITE_BASE_API_URL
      ).replace("/meet/", "/admincenter/")}/v1/users/${userId}/image`;
    },
    []
  );

  const [isActionDrawerOpen, setIsActionDrawerOpen] = useState(false);
  const [selectedActionItem, setSelectedActionItem] =
    useState<IActionItems | null>(null);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskActionItem, setTaskActionItem] = useState<IActionItems | null>(
    null
  );

  const { mutate: deleteActionItem, isPending: isDeletingActionItem } =
    useRecapDeleteActionItem();

  const { mutate: undoDeleteActionItem, isPending: isRestoringActionItem } =
    useRecapUndoDeleteActionItem();

  const onOpenActionDrawer = (item: IActionItems) => {
    setSelectedActionItem(item);
    setIsActionDrawerOpen(true);
  };

  const onCloseActionDrawer = () => {
    setSelectedActionItem(null);
    setIsActionDrawerOpen(false);
  };

  const onEditClicked = () => {
    if (selectedActionItem) {
      onEdit?.(selectedActionItem);
    }
    onCloseActionDrawer();
  };

  const onDelete = (record?: IActionItems | null) => {
    if (!sessionId || !record?.id) return;

    deleteActionItem(
      {
        sessionId,
        id: record?.id,
      },
      {
        onSuccess: () => {
          onCloseActionDrawer();
        },
      }
    );
  };

  const onRestore = (record?: IActionItems | null) => {
    if (!sessionId || !record?.id) return;

    undoDeleteActionItem(
      {
        sessionId,
        id: record?.id,
      },
      {
        onSuccess: () => {
          onCloseActionDrawer();
        },
      }
    );
  };

  const onConvertToTask = (item: IActionItems) => {
    setTaskActionItem(item);
    setIsTaskModalOpen(true);
    onCloseActionDrawer();
  };

  const onCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setTaskActionItem(null);
  };

  const onSaveStatus = (_type, _text) => {
    console.log("onSaveStatus", _type, _text);

    if (_type === "success") {
      setMessage({
        type: "success",
        text: t("recap.convertToTaskSuccess"),
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.recapActionItemsBySessionId, sessionId],
      });

      onCloseTaskModal();
    } else if (_text) {
      setMessage({
        type: "error",
        text: _text,
      });
    }
  };

  const tableTitle = useMemo(
    () => (title: string) => <Text className="font-weight-500">{title}</Text>,
    []
  );

  if (isLoading) {
    return <RecapActionItemsSkeleton />;
  }

  if (!actionItems?.length) {
    return (
      <Flex justify="center">
        <Empty
          title={t(
            showDeleted
              ? "recap.emptyDeletedActionItems"
              : "recap.emptyActionItems"
          )}
          icon={<Image src={EmptyImage} preview={false} />}
        />
      </Flex>
    );
  }

  const columns = [
    {
      title: "#",
      key: "rowIndex",
      width: 50,
      align: "center" as const,
      render: (_: unknown, _record: IActionItems, index: number) => (
        <Text>{index + 1}</Text>
      ),
    },
    {
      title: tableTitle(t("recap.title")),
      dataIndex: "title",
      key: "title",
      width: 380,
      render: (text: string, _record: IActionItems, index) => {
        return (
          <Flex align="flex-start" justify="center" gap={5} vertical>
            <Flex align="center" gap={8}>
              {_record?.createdFromRosa && (
                <IconGenerator name={"RosaLogo"} size={16} />
              )}
              <Paragraph
                ellipsis={{
                  rows: 2,
                  tooltip: text,
                }}
                className="h7 m-0"
              >
                {text}
                {_record?.updateDate && _record?.updateDate !== null && (
                  <span>
                    {""}
                    <Text className="h8 mx-2" type="secondary">
                      ( {t("recap.edited")})
                    </Text>
                  </span>
                )}
              </Paragraph>
            </Flex>
            <Paragraph
              className="h7 m-0"
              type="secondary"
              ellipsis={{
                rows: 1,
                tooltip: _record?.description,
              }}
              style={{ maxWidth: "100%" }}
            >
              {_record?.description}
            </Paragraph>
          </Flex>
        );
      },
    },
    {
      title: tableTitle(t("recap.assignee")),
      dataIndex: "assignee",
      key: "assignee",
      width: 200,
      render: (_: unknown, item: IActionItems) => (
        <>
          {(item?.user?.fullName ?? item?.assigneeName) ? (
            <Person
              title={item?.user?.fullName ?? item?.assigneeName}
              thumbUrl={getUserImage(item?.userId || item?.user?.id)}
              description={item?.user?.jobTitle || null}
              classes={{
                title: "h7",
                description: "h7 ",
              }}
            />
          ) : (
            <Text type="secondary">{t("recap.unassigned")}</Text>
          )}
        </>
      ),
    },
    {
      title: tableTitle(t("recap.priority")),
      dataIndex: "priority",
      key: "priority",
      width: 140,
      render: (_: unknown, item: IActionItems) => {
        const meta = getPriorityMeta(item?.priority);
        if (!meta) {
          return <Text type="secondary">{t("recap.noPriority")}</Text>;
        }
        return (
          <Flex align="center" gap={6}>
            <IconGenerator
              name="UilFlag"
              size={16}
              color={priorityColorVar[meta.color]}
            />
            <Text>{t(meta.key)}</Text>
          </Flex>
        );
      },
    },
    {
      title: tableTitle(t("recap.dueDate")),
      dataIndex: "dueDate",
      key: "dueDate",
      width: 140,
      render: (_: unknown, item: IActionItems) => {
        if (!item?.dueDate) {
          return (
            <Text type="secondary" className="h7">
              {t("recap.noDueDate")}
            </Text>
          );
        }
        const formatted = moment(item.dueDate).format(
          DateFormats.JDayMonthDate
        );
        return <Text className="h7">{formatted}</Text>;
      },
    },
    {
      title: tableTitle(t("recap.taskNumber")),
      dataIndex: "taskCode",
      key: "taskCode",
      width: 140,
      render: (_, item: IActionItems) => {
        if (
          item?.taskCode === null ||
          item?.taskCode === undefined ||
          item?.taskCode === ""
        ) {
          if (item?.isDeleted) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Button
              type="link"
              size="small"
              className="p-0"
              onClick={() => onConvertToTask(item)}
            >
              {t("recap.convertToTask")}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            className="p-0"
            onClick={() => openTaskUrl(item.taskUrl)}
          >
            #{String(item.taskCode)}
          </Button>
        );
      },
    },
    {
      title: tableTitle(t("general.action")),
      key: "action",
      width: 80,
      align: "center" as const,
      render: (_: unknown, record: IActionItems) => {
        return (
          <Flex justify="center" align="center" gap={8}>
            {showDeleted ? (
              <Tooltip title={t("recap.cannotEditDeleted")}>
                <Button
                  type="text"
                  size="small"
                  icon={
                    <Icon
                      name="UilEditAlt"
                      size={16}
                      color="var(--color-disabled)"
                    />
                  }
                  disabled
                />
              </Tooltip>
            ) : (
              <Tooltip title={t("actions.edit")}>
                <Button
                  type="text"
                  size="small"
                  icon={
                    <Icon
                      name="UilEditAlt"
                      size={16}
                      color={
                        record?.taskCode ? "var(--color-disabled)" : "primary"
                      }
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(record);
                  }}
                  disabled={!!record?.taskCode}
                />
              </Tooltip>
            )}

            {showDeleted ? (
              <ConfirmPopoverButton
                item={record}
                onConfirm={onRestore}
                disabled={isRestoringActionItem}
                iconName="UilRedo"
                variant="primary"
                confirmTextKey="recap.restoreConfirm"
                buttonTextKey="actions.restore"
                tooltipKey="actions.restore"
              />
            ) : (
              <Tooltip title={t("actions.delete")}>
                <DeleteConfirmButton
                  item={record}
                  onDeleteConfirm={onDelete}
                  disabled={isDeletingActionItem || !!record?.taskCode}
                  withTitle={false}
                  className="delete-button"
                />
              </Tooltip>
            )}
          </Flex>
        );
      },
    },
  ];

  if (isResponsive) {
    return (
      <>
        <div>
          <List
            className="recap-action-items"
            dataSource={actionItems}
            split={false}
            renderItem={(item: IActionItems, index) => {
              const priorityMeta = getPriorityMeta(item?.priority);
              return (
                <List.Item key={item?.id}>
                  <Flex align="stretch" gap={8} className="w-100">
                    <Flex align="center" className="recap-action-items__index">
                      <Text>{index + 1}</Text>
                    </Flex>

                    <div className="recap-action-items__line" />

                    <Flex vertical gap={8} flex={1}>
                      <Flex align="flex-start" justify="space-between" gap={8}>
                        <Text className="h7">
                          {item?.createdFromRosa && (
                            <span className="rosa-icon">
                              <IconGenerator name={"RosaLogo"} size={16} />
                            </span>
                          )}
                          {item?.title}
                        </Text>

                        <Button
                          type="text"
                          size="small"
                          icon={
                            <Icon
                              name="UilEllipsisV"
                              size={16}
                              color="textColor"
                            />
                          }
                          onClick={() => onOpenActionDrawer(item)}
                        />
                      </Flex>

                      <Flex align="center" justify="space-between" gap={8}>
                        {(item?.user?.fullName ?? item?.assigneeName) ? (
                          <Flex gap={4} align="center" style={{ minWidth: 0 }}>
                            <Text type="secondary" className="h8">
                              {t("recap.assigneesTo")}
                            </Text>

                            <Text
                              className="h8"
                              ellipsis={{
                                tooltip:
                                  item?.user?.fullName ?? item?.assigneeName,
                              }}
                            >
                              {item?.user?.fullName ?? item?.assigneeName}
                            </Text>
                          </Flex>
                        ) : (
                          <Text type="secondary" className="h8">
                            {t("recap.unassigned")}
                          </Text>
                        )}

                        {priorityMeta && (
                          <Flex align="center" gap={6} className="shrink-0">
                            <IconGenerator
                              name="UilFlag"
                              size={16}
                              color={priorityColorVar[priorityMeta.color]}
                            />
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>
                </List.Item>
              );
            }}
          />
        </div>
        <Drawer
          open={isActionDrawerOpen}
          placement="bottom"
          onClose={onCloseActionDrawer}
          closable={false}
          height="auto"
          rootClassName="recap-action-items__drawer"
        >
          <Flex vertical align="flex-start">
            {!showDeleted && (
              <>
                <Button
                  className="p-0"
                  type="text"
                  icon={<Icon name="UilBagAlt" size={24} color="primary" />}
                  onClick={() =>
                    selectedActionItem && onConvertToTask(selectedActionItem)
                  }
                >
                  {t("recap.convertToTask")}
                </Button>

                <Button
                  className="p-0"
                  type="text"
                  icon={<Icon name="UilEditAlt" size={24} color="primary" />}
                  onClick={onEditClicked}
                >
                  {t("actions.edit")}
                </Button>

                <ConfirmPopoverButton
                  className="p-0"
                  item={selectedActionItem}
                  onConfirm={onDelete}
                  disabled={isDeletingActionItem}
                  withTitle
                  iconSize={24}
                  iconName="UilTrashAlt"
                  iconColor="error"
                />
              </>
            )}

            {showDeleted && (
              <ConfirmPopoverButton
                className="p-0"
                item={selectedActionItem}
                onConfirm={onRestore}
                disabled={isRestoringActionItem}
                withTitle
                iconSize={24}
                iconName="UilRedo"
                iconColor="primary"
                variant="primary"
                confirmTextKey="recap.restoreConfirm"
                buttonTextKey="actions.restore"
              />
            )}
          </Flex>
        </Drawer>
        <ConvertToTask
          open={isTaskModalOpen}
          actionItem={taskActionItem}
          sessionId={sessionId}
          onCloseModal={onCloseTaskModal}
          onSaveStatus={onSaveStatus}
        />
      </>
    );
  }

  return (
    <>
      <Table
        dataSource={actionItems}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1100, y: 500 }}
        tableLayout="fixed"
      />
      <ConvertToTask
        open={isTaskModalOpen}
        actionItem={taskActionItem}
        sessionId={sessionId}
        onCloseModal={onCloseTaskModal}
        onSaveStatus={onSaveStatus}
      />
    </>
  );
};

export default ActionItems;
